import React, { useContext } from 'react';
import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { getBreakpointFromManor } from 'ctm-components';

import { Typography } from '@comparethemarketau/manor-typography';
import { withQuoteFlatten } from '../../products/products/common/with_quote_flatten';

import { SectionTitle } from '../common/section_title';
import { ContentCard } from '../common/content_card';
import { ContentTitleText } from '../common/content_title_text';

const useStyles = createUseStyles((theme) => ({
    container: {
        marginBottom: theme.spacing[40]
    },
    excessItem: {
        display: 'flex',
        margin: '0.6rem 0'
    },
    excesses: {
        display: 'inline-block'
    },
    excessText: {
        fontSize: theme.fontSize.sm,
        fontWeight: theme.fontWeight.normal,
        lineHeight: theme.lineHeight.normal,
        textAlign: 'left',
        width: '80%',
        '& p': {
            color: theme.colors.grey700,
            fontSize: theme.fontSize.xs,
            lineHeight: theme.lineHeight.tight,
            margin: '0.4rem 0'
        }
    },
    helperText: {
        extend: ({ breakpoint }) => {
            switch (breakpoint) {
                case 'xs': return { width: '100%' };
                case 'md': return { width: '80%' };
                default: return { width: '80%' };
            }
        }
    },
    excessAmount: {
        fontSize: theme.fontSize.base,
        fontWeight: theme.fontWeight.bold,
        lineHeight: theme.lineHeight.normal,
        textAlign: 'right',
        width: '20%'
    }
}));

const _PolicyExcesses = ({ className, config, productExcess, productGlassExcess, productAdditionalExcesses }) => {
    const breakpoint = getBreakpointFromManor();
    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme, breakpoint });
    const { id, label } = config;

    return (
        <div id={id} className={classNames('test-policyProduct-policyExceses', classes.container, className)}>
            <SectionTitle label={label} />
            <ContentCard>
                <ContentTitleText
                    title="Excesses"
                    subTitle="The basic excess is a financial contribution which you may need to pay when making a claim.
                    Some insurers have a glass excess that may apply to certain claims instead of the basic excess." />
                <Typography component="div" className={classNames(classes.excesses, classes.excessText)}>Basic Excess</Typography>
                <Typography component="div" className={classNames(classes.excesses, classes.excessAmount)}>{`$${productExcess}`}</Typography>
                {productGlassExcess
                    && (
                        <>
                            <Typography component="div" className={classNames(classes.excesses, classes.excessText)}>Glass Excess</Typography>
                            <Typography component="div" className={classNames(classes.excesses, classes.excessAmount)}>{`$${productGlassExcess}`}</Typography>
                        </>
                    )}

            </ContentCard>
            <ContentCard>
                <ContentTitleText
                    title="Additional Excesses"
                    subTitle="Additional excesses apply to specific circumstances on top of the basic excess."
                    textAlignVariant="left" />
                {productAdditionalExcesses ? (productAdditionalExcesses.map((element) => {
                    const excessText = element.get('description').replace('(', '\n').replace(')', '\n');
                    const desc = excessText.split('\n')[0];
                    const helperText = excessText.split('\n')[1];
                    const excessAmount = element.get('amount').replace('$', '');
                    return (
                        <div key={excessText} className={classes.excessItem}>
                            <Typography component="div" className={classes.excessText}>
                                {desc}
                                { helperText
                                && <Typography component="div" className={classNames(classes.helperText, classes.excessText)}><p>{helperText}</p></Typography>}
                            </Typography>
                            <Typography component="div" className={classes.excessAmount}>{`$${excessAmount}`}</Typography>
                        </div>
                    );
                })) : 'Refer to PDS'}

            </ContentCard>
        </div>
    );
};

_PolicyExcesses.propTypes = {
    className: PropTypes.string,
    config: PropTypes.object.isRequired,
    productExcess: PropTypes.number,
    productGlassExcess: PropTypes.number,
    productAdditionalExcesses: PropTypes.object
};

const PolicyExcesses = withQuoteFlatten(_PolicyExcesses);

export { PolicyExcesses };
